import { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, ROLE_LABELS } from "@/lib/format";
import PhotoUploader from "@/components/PhotoUploader";
import { Plus, UserCircle, PencilSimple, Trash } from "@phosphor-icons/react";

const CREATABLE_ROLES = [
  "director","study_director","secretary","accountant","cashier","hr",
  "teacher","supervisor","librarian","stock_manager","parent","auditor",
];

const resolveUrl = (u) => u ? (u.startsWith("http") || u.startsWith("data:") ? u : `${API_BASE.replace("/api", "")}${u}`) : null;

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "teacher", phone: "", avatar_url: "" });

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ email: "", password: "", name: "", role: "teacher", phone: "", avatar_url: "" }); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, role: u.role, phone: u.phone || "", avatar_url: u.avatar_url || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const patch = { name: form.name, role: form.role, phone: form.phone, avatar_url: form.avatar_url };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${editing.id}`, patch);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/auth/register", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} ? Cette action est journalisée.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utilisateur supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const grouped = users.reduce((acc, u) => {
    (acc[u.role] = acc[u.role] || []).push(u); return acc;
  }, {});

  const canEdit = (u) => {
    if (me.role === "super_admin") return true;
    if (u.role === "super_admin" || u.role === "owner") return false;
    return true;
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Équipe</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Utilisateurs & Rôles</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openCreate} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-user"><Plus size={16} className="mr-2" /> Nouvel utilisateur</Button></DialogTrigger>
          <DialogContent data-testid="dialog-user">
            <DialogHeader><DialogTitle>{editing ? `Modifier ${editing.name}` : "Créer un utilisateur"}</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label>Photo de profil</Label>
                <div className="mt-1.5">
                  <PhotoUploader value={form.avatar_url} onChange={v => setForm({ ...form, avatar_url: v })} kind="avatar" size={72} />
                </div>
              </div>
              <div><Label>Nom complet</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="input-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-user-email" /></div>
              <div><Label>{editing ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe temporaire"}</Label><Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="input-user-password" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+226 70..." /></div>
              <div>
                <Label>Rôle</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{CREATABLE_ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-user">{editing ? "Mettre à jour" : "Créer"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(grouped).map(([role, us]) => (
          <div key={role} className="bg-card border border-border rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-3">{ROLE_LABELS[role] || role} · {us.length}</div>
            <div className="space-y-2">
              {us.map(u => (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors" data-testid={`user-row-${u.id}`}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                    {u.avatar_url ? <img src={resolveUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <UserCircle size={22} weight="duotone" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  {canEdit(u) && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`btn-edit-user-${u.id}`}><PencilSimple size={14} /></Button>
                      {u.id !== me.id && <Button variant="ghost" size="icon" onClick={() => del(u)} data-testid={`btn-delete-user-${u.id}`}><Trash size={14} className="text-destructive" /></Button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
